import type React from "react";
import { useState } from "react";

interface FAQ {
  q: string;
  a: string;
}

const FAQSection: React.FC = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const faqs: FAQ[] = [
    {
      q: "予約は必要ですか？当日でも行けますか？",
      a: "当店は【予約優先制】となっております。事前のご予約をおすすめしておりますが、空きがあれば当日のご案内も可能です。お気軽にお電話・LINEにてお問い合わせください。",
    },
    {
      q: "遅刻しそうな場合はどうすればいいですか？",
      a: "ご来店が遅れた場合、次のお客様のご予約状況により施術時間を短縮させていただく場合がございます。お時間に間に合わないときは、お電話にてご連絡ください。",
    },
    {
      q: "着替えは有料ですか？",
      a: "お着替えは無料でご用意しております。サイズも各種取り揃えておりますのでご安心ください。",
    },
    {
      q: "持病があるのですが、受けても大丈夫ですか？",
      a: "高血圧・心疾患・重度の糖尿病・骨粗しょう症・皮膚疾患・発熱・飲酒されている場合などは、施術をお断り、もしくは一部制限させていただくことがございます。ご心配な場合は、事前に医師にご相談のうえ、お問い合わせください。",
    },
    {
      q: "支払い方法は何がありますか？",
      a: "お支払いは 現金 または PayPay のみご利用いただけます。",
    },
  ];

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <section className="pt-12 pb-16 md:pt-20 md:pb-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-yellow-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob"></div>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-pink-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>

      <div className="max-w-3xl mx-auto px-4 relative z-10">
        <div className="text-center mb-10">
          <h3 className="text-2xl md:text-4xl font-black text-gray-900 leading-tight">
            よくある質問
          </h3>
        </div>

        <div className="space-y-4">
          {faqs.map((item, index) => (
            <div
              key={item.q}
              className={`bg-white rounded-xl transition-all duration-300 border-2 ${
                openFaqIndex === index
                  ? "border-orange-400 shadow-lg scale-[1.01]"
                  : "border-gray-100 hover:border-orange-200 hover:shadow-md"
              }`}
            >
              <button
                type="button"
                onClick={() => toggleFaq(index)}
                className="w-full text-left p-4 md:p-5 flex justify-between items-center"
              >
                <div className="flex gap-4 items-start pr-4">
                  <span
                    className={`font-black text-lg shrink-0 ${
                      openFaqIndex === index
                        ? "text-orange-500"
                        : "text-gray-300"
                    }`}
                  >
                    Q.
                  </span>
                  <span
                    className={`font-bold text-sm md:text-base leading-snug pt-0.5 ${
                      openFaqIndex === index ? "text-gray-900" : "text-gray-600"
                    }`}
                  >
                    {item.q}
                  </span>
                </div>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                    openFaqIndex === index
                      ? "bg-orange-500 rotate-180"
                      : "bg-gray-100"
                  }`}
                >
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={3}
                    stroke="currentColor"
                    className={`w-3 h-3 ${
                      openFaqIndex === index ? "text-white" : "text-gray-400"
                    }`}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m19.5 8.25-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </div>
              </button>
              {openFaqIndex === index && (
                <div className="px-4 md:px-5 pb-6 pt-0 animate-fade-in">
                  <div className="pl-8 flex gap-3">
                    <span className="font-black text-lg text-orange-500 shrink-0">
                      A.
                    </span>
                    <div className="text-gray-600 text-sm leading-relaxed font-medium">
                      {item.a}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default FAQSection;
